import React, { useState, useCallback } from 'react';
import {
  TrendingUp,
  Clock,
  FileText,
  AlertCircle,
  CheckCircle,
  Plus,
  BarChart3,
  Calendar,
  Search,
  Eye,
  UserCheck,
  ChevronRight
} from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/Card';
import { Button } from '../ui/Button';
import { Badge } from '../ui/Badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../ui/Tabs';
import { useAuth } from '../../hooks/useAuth';
import { useNavigate } from 'react-router-dom';
import { useDirectDashboardStats } from '../../hooks/useDirectSupabaseQuotes';
import { formatUSD } from '../../utils/formatting';
import { SearchModal } from './SearchModal';
import { MetricsGrid } from './MetricsGrid';

export const Dashboard = React.memo(function Dashboard() {
  const { user } = useAuth();
  const navigate = useNavigate();

  // Direct Supabase query - bypasses Railway API for faster loading
  const { stats, quotes, isLoading: loading, error } = useDirectDashboardStats();

  // Map stats to agentStats format for backward compatibility
  const agentStats = {
    myQuotes: stats.myQuotes,
    myQuotesValue: stats.myQuotesValue,
    thisMonthQuotes: stats.thisMonthQuotes,
    thisMonthValue: stats.thisMonthValue,
    expiringQuotes: stats.expiringQuotes,
    thisWeekQuotes: stats.thisWeekQuotes,
    recentQuotes: stats.recentQuotes || [],
    // These metrics require additional tracking - showing as null until implemented
    successRate: 0,
    avgResponseTime: null as number | null,
    customerSatisfaction: null as number | null,
    quoteAccuracy: null as number | null,
    followUpRate: null as number | null
  };

  // Search functionality state
  const [showSearchModal, setShowSearchModal] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState<Array<Record<string, unknown>>>([]);
  const [searchLoading, setSearchLoading] = useState(false);


  // Search functionality - memoized to prevent recreation on each render
  const handleSearch = useCallback((query: string) => {
    setSearchQuery(query);
    if (!query.trim()) {
      setSearchResults([]);
      return;
    }

    setSearchLoading(true);
    try {
      // Search through existing quotes for customers
      const q = query.toLowerCase();
      const results = quotes.filter(quote =>
        (quote.customerName as string)?.toLowerCase().includes(q) ||
        (quote?.customerContact as Record<string, unknown>)?.email?.toString().toLowerCase().includes(q) ||
        (quote.id as string)?.toLowerCase().includes(q)
      );

      setSearchResults(results);
    } catch (error) {
      console.error('Search failed:', error);
      setSearchResults([]);
    } finally {
      setSearchLoading(false);
    }
  }, [quotes]);

  // Log error if direct query fails (fallback would go here in future)
  if (error) {
    console.error('[Dashboard] Direct Supabase query failed:', error);
  }

  // Agent/User Dashboard
  if (user?.role === 'user' || user?.role === 'agent' || user?.role === 'admin' || user?.role === 'superadmin' || user?.role === 'company_admin') {
    return (
      <div className="w-full space-y-6 relative">
        {/* Subtle background gradient */}
        <div className="absolute inset-0 bg-gradient-to-br from-background via-background to-accent/5 pointer-events-none"></div>
        <div className="relative z-10 space-y-6">
          {/* Header - Enhanced */}
          <div className="flex w-full flex-col gap-3 border-b border-border pb-8">
            <div className="flex items-center justify-between">
              <div>
                <h1 className="text-3xl font-bold">
                  <span className="text-foreground">
                    Welcome back, {(user?.name || 'Agent').replace(' (Super Admin)', '').replace('Sahal (Super Admin)', 'Sahal')}
                  </span>
                  {user?.role === 'superadmin' && (
                    <span className="text-accent font-bold"> Super Admin</span>
                  )}
                  <span className="text-foreground">!</span>
                  <span className="ml-2">👋</span>
                </h1>
                <p className="text-muted-foreground mt-1 text-lg">
                  {user?.role === 'superadmin' 
                    ? 'Here\'s your comprehensive system overview and performance insights'
                    : 'Here\'s your personal performance overview and quick actions'
                  }
                </p>
              </div>
              <div className="hidden lg:flex items-center gap-2 text-sm text-muted-foreground">
                <Calendar className="h-4 w-4" />
                {new Date().toLocaleDateString('en-US', { 
                  weekday: 'long', 
                  year: 'numeric', 
                  month: 'long', 
                  day: 'numeric' 
                })}
              </div>
            </div>
          </div>

          {/* Quick Actions - Clean & Simple */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
            <Button onClick={() => navigate('/calculator')} className="flex items-center gap-2 h-12 bg-gradient-to-r from-accent to-accent/90 hover:from-accent/90 hover:to-accent shadow-lg hover:shadow-xl transition-all duration-200 text-accent-foreground">
              <Plus className="h-4 w-4" />
              Create New Quote
            </Button>
            <Button variant="outline" onClick={() => navigate('/quotes')} className="flex items-center gap-2 h-12 border-2 hover:border-accent hover:bg-accent/10 hover:text-accent transition-all duration-200">
              <Eye className="h-4 w-4" />
              View My Quotes
            </Button>
            <Button 
              variant="outline" 
              className="flex items-center gap-2 h-12 border-2 hover:border-accent hover:bg-accent/10 hover:text-accent transition-all duration-200"
              onClick={() => setShowSearchModal(true)}
            >
              <Search className="h-4 w-4" />
              Search Customers
            </Button>
          </div>

        {/* Billing Status - Temporarily disabled until API is implemented */}
        {/* <BillingStatusCard /> */}

        {/* Personal Performance Metrics */}
        <div className="space-y-6">
          <div className="flex items-center gap-3">
            <div className="w-1 h-6 bg-accent rounded-full"></div>
            <h2 className="text-xl font-semibold">Performance Overview</h2>
          </div>
          <MetricsGrid
            loading={loading}
            myQuotes={agentStats.myQuotes}
            thisMonthQuotes={agentStats.thisMonthQuotes}
            myQuotesValue={agentStats.myQuotesValue}
            thisMonthValue={agentStats.thisMonthValue}
            thisWeekQuotes={agentStats.thisWeekQuotes}
          />
        </div>

        {/* Enhanced Analytics with Tabs */}
        <Card className="border-border bg-card hover:shadow-lg hover:shadow-accent/5 transition-all duration-300 hover:border-accent/20">
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-card-foreground">
              <BarChart3 className="h-5 w-5 text-accent" />
              Analytics & Insights
            </CardTitle>
            <CardDescription className="text-muted-foreground">
              Detailed performance metrics and trends
            </CardDescription>
          </CardHeader>
          <CardContent>
            <Tabs defaultValue="overview" className="w-full">
              <TabsList className="grid w-full grid-cols-4">
                <TabsTrigger value="overview">Overview</TabsTrigger>
                <TabsTrigger value="trends">Trends</TabsTrigger>
                <TabsTrigger value="products">Products</TabsTrigger>
                <TabsTrigger value="performance">Performance</TabsTrigger>
              </TabsList>
              
              <TabsContent value="overview" className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="space-y-3">
                    <div className="flex items-center justify-between">
                      <span className="text-sm font-medium">This Month</span>
                      <span className="text-sm text-muted-foreground">
                        {loading ? '-' : agentStats.thisMonthQuotes} quotes
                      </span>
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="text-sm font-medium">Active Quotes</span>
                      <span className="text-sm text-muted-foreground">
                        {loading ? '-' : agentStats.myQuotes - agentStats.expiringQuotes}
                      </span>
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="text-sm font-medium">Success Rate</span>
                      <span className="text-sm text-muted-foreground">Coming Soon</span>
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="text-sm font-medium">Month Revenue</span>
                      <span className="text-sm text-muted-foreground">Coming Soon</span>
                    </div>
                  </div>
                  
                  <div className="space-y-3">
                    <div className="flex items-center justify-between">
                      <span className="text-sm font-medium">Response Time</span>
                      <span className="text-sm text-muted-foreground">
                        {loading ? '-' : agentStats.avgResponseTime ? `${agentStats.avgResponseTime} hrs` : 'Coming Soon'}
                      </span>
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="text-sm font-medium">Customer Satisfaction</span>
                      <span className="text-sm text-muted-foreground">
                        {loading ? '-' : agentStats.customerSatisfaction ? `${agentStats.customerSatisfaction}/5` : 'Coming Soon'}
                      </span>
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="text-sm font-medium">Quote Accuracy</span>
                      <span className="text-sm text-muted-foreground">
                        {loading ? '-' : agentStats.quoteAccuracy ? `${agentStats.quoteAccuracy}%` : 'Coming Soon'}
                      </span>
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="text-sm font-medium">Follow-up Rate</span>
                      <span className="text-sm text-muted-foreground">
                        {loading ? '-' : agentStats.followUpRate ? `${agentStats.followUpRate}%` : 'Coming Soon'}
                      </span>
                    </div>
                  </div>
                </div>
              </TabsContent>
              
              <TabsContent value="trends" className="space-y-4">
                <div className="flex items-center justify-center h-64">
                  <div className="text-center">
                    <div className="inline-flex items-center justify-center w-12 h-12 mb-4 rounded-full bg-muted/20">
                      <BarChart3 className="w-6 h-6 text-muted-foreground" />
                    </div>
                    <h3 className="text-lg font-semibold text-foreground mb-2">Trends Analytics</h3>
                    <p className="text-sm text-muted-foreground">Coming Soon</p>
                    <p className="text-xs text-muted-foreground mt-2">Advanced trend analysis and forecasting will be available soon</p>
                  </div>
                </div>
              </TabsContent>
              
              <TabsContent value="products" className="space-y-4">
                <div className="flex items-center justify-center h-64">
                  <div className="text-center">
                    <div className="inline-flex items-center justify-center w-12 h-12 mb-4 rounded-full bg-muted/20">
                      <FileText className="w-6 h-6 text-muted-foreground" />
                    </div>
                    <h3 className="text-lg font-semibold text-foreground mb-2">Product Analytics</h3>
                    <p className="text-sm text-muted-foreground">Coming Soon</p>
                    <p className="text-xs text-muted-foreground mt-2">Detailed product performance and material insights will be available soon</p>
                  </div>
                </div>
              </TabsContent>
              
              <TabsContent value="performance" className="space-y-4">
                <div className="flex items-center justify-center h-64">
                  <div className="text-center">
                    <div className="inline-flex items-center justify-center w-12 h-12 mb-4 rounded-full bg-muted/20">
                      <TrendingUp className="w-6 h-6 text-muted-foreground" />
                    </div>
                    <h3 className="text-lg font-semibold text-foreground mb-2">Performance Analytics</h3>
                    <p className="text-sm text-muted-foreground">Coming Soon</p>
                    <p className="text-xs text-muted-foreground mt-2">Advanced performance tracking and activity analytics will be available soon</p>
                  </div>
                </div>
              </TabsContent>
            </Tabs>
          </CardContent>
        </Card>

        {/* Recent Activity & Alerts */}
        <div className="grid gap-6 md:grid-cols-2">
          <Card className="border-border bg-card hover:shadow-lg hover:shadow-accent/5 transition-all duration-300 hover:border-accent/20">
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-card-foreground">
                <Clock className="h-5 w-5 text-accent" />
                Recent Quotes
              </CardTitle>
              <CardDescription className="text-muted-foreground">
                Your latest quote activity
              </CardDescription>
            </CardHeader>
            <CardContent>
              {loading ? (
                <div className="space-y-3">
                  {[1,2,3].map(i => (
                    <div key={i} className="flex items-center gap-3">
                      <div className="w-2 h-2 bg-muted rounded-full animate-pulse"></div>
                      <div className="h-4 bg-muted rounded flex-1 animate-pulse"></div>
                    </div>
                  ))}
                </div>
              ) : agentStats.recentQuotes.length > 0 ? (
                <div className="space-y-3">
                  {agentStats.recentQuotes.map((quote) => (
                    <div 
                      key={quote.id} 
                      className="flex items-center justify-between p-3 rounded-lg hover:bg-muted/50 transition-all duration-200 cursor-pointer group border border-transparent hover:border-accent/20"
                      onClick={() => navigate(`/quotes/${quote.id}`)}
                      role="button"
                      tabIndex={0}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter' || e.key === ' ') {
                          e.preventDefault();
                          navigate(`/quotes/${quote.id}`);
                        }
                      }}
                    >
                      <div className="flex items-center gap-3">
                        <div className="w-2 h-2 bg-accent rounded-full group-hover:scale-125 transition-transform duration-200"></div>
                        <div>
                          <span className="text-sm font-medium group-hover:text-accent transition-colors duration-200">{quote.customerName}</span>
                          <p className="text-xs text-muted-foreground">
                            {new Date(quote.createdAt).toLocaleDateString()}
                          </p>
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        <Badge variant="secondary" className="text-xs group-hover:bg-accent/10 transition-colors duration-200">
                          {formatUSD(quote.totalWithProfit || quote.totalWithMargin || quote.breakdown?.total || 0)}
                        </Badge>
                        <ChevronRight className="h-4 w-4 text-muted-foreground opacity-0 group-hover:opacity-100 transition-all duration-200" />
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-6">
                  <FileText className="h-8 w-8 text-muted-foreground mx-auto mb-2" />
                  <p className="text-sm text-muted-foreground">No quotes yet</p>
                  <p className="text-xs text-muted-foreground">Create your first quote to see it here</p>
                </div>
              )}
            </CardContent>
          </Card>

          <Card className="border-border bg-card hover:shadow-lg hover:shadow-accent/5 transition-all duration-300 hover:border-accent/20">
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-card-foreground">
                <AlertCircle className="h-5 w-5 text-warning" />
                Alerts & Notifications
              </CardTitle>
              <CardDescription className="text-muted-foreground">
                Important updates and reminders
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-3">
                {agentStats.myQuotes === 0 && !loading && (
                  <div className="text-center py-4">
                    <AlertCircle className="h-8 w-8 text-muted-foreground mx-auto mb-2" />
                    <p className="text-sm text-muted-foreground">No alerts or notifications</p>
                    <p className="text-xs text-muted-foreground">Create your first quote to see activity here</p>
                  </div>
                )}
                <div className="flex items-center gap-3 p-3 bg-warning/10 rounded-lg border border-warning/20 hover:bg-warning/15 transition-colors">
                  <AlertCircle className="h-4 w-4 text-warning" />
                  <div className="flex-1">
                    <p className="text-sm font-medium text-card-foreground">
                      {loading ? '-' : agentStats.expiringQuotes} quotes expiring soon
                    </p>
                    <p className="text-xs text-muted-foreground">
                      Review and follow up with customers
                    </p>
                  </div>
                </div>
                
                {agentStats.recentQuotes.length > 0 && (
                  <div className="flex items-center gap-3 p-3 bg-info/10 rounded-lg border border-info/20 hover:bg-info/15 transition-colors">
                    <UserCheck className="h-4 w-4 text-info" />
                    <div className="flex-1">
                      <p className="text-sm font-medium text-card-foreground">
                        Recent quote activity
                      </p>
                      <p className="text-xs text-muted-foreground">
                        {agentStats.recentQuotes[0]?.customerName} - {formatUSD(agentStats.recentQuotes[0]?.totalWithProfit || agentStats.recentQuotes[0]?.totalWithMargin || agentStats.recentQuotes[0]?.breakdown?.total || 0)}
                      </p>
                    </div>
                  </div>
                )}
                
                {agentStats.thisMonthQuotes > 0 && (
                  <div className="flex items-center gap-3 p-3 bg-success/10 rounded-lg border border-success/20 hover:bg-success/15 transition-colors">
                    <CheckCircle className="h-4 w-4 text-success" />
                    <div className="flex-1">
                      <p className="text-sm font-medium text-card-foreground">
                        Monthly performance
                      </p>
                      <p className="text-xs text-muted-foreground">
                        {agentStats.thisMonthQuotes} quotes created this month - {formatUSD(agentStats.thisMonthValue)} total value
                      </p>
                    </div>
                  </div>
                )}
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Search Modal */}
        <SearchModal
          isOpen={showSearchModal}
          onClose={() => setShowSearchModal(false)}
          searchQuery={searchQuery}
          onSearchChange={handleSearch}
          searchResults={searchResults}
          searchLoading={searchLoading}
        />
        </div>
      </div>
    );
  }
  
  // Fallback for other roles (this should not be reached with current logic)
  return (
    <div className="w-full space-y-8">
      <div className="flex w-full flex-col gap-1 border-b border-border py-6">
        <h1 className="text-2xl font-bold">Dashboard</h1>
        <p className="max-w-xl text-muted-foreground">
          Dashboard access not available for your role
        </p>
      </div>
    </div>
  );
});
